import React, { useMemo } from "react";
import { map } from "lodash";
import LineChart from "@charty/line-plot";
import { Color, content, isColor, useTheme } from "@glossy";
import { getMetricLabel } from "@linkfluence/i18n";
import { AbstractDate } from "@linkfluence/models/date";
import { Metric } from "@linkfluence/models/metric";
import { useCurrentUserLocale } from "@radarly/core/user/use-cases";
import { tr } from "@radarly/i18n";
import { DatavizLineChart } from "@radarly/models/dataviz";
import { SearchFragmentDateMetrics } from "@radarly/models/search";
import {
  useComparaisonPeriodFromFragments,
  useRangeFromFragment,
  useRangeWithTimeUnit,
  useTimezoneFromFragment,
} from "../../data";
import LineChartTooltip from "../../tooltip/line-chart";
import Tooltip from "../../tooltip/metric";

export interface SharedWidgetsMetricLineChartInnerProps {
  fragment: SearchFragmentDateMetrics;
  dataviz: DatavizLineChart;
  comparaisonData: SearchFragmentDateMetrics["data"];
  data: SearchFragmentDateMetrics["data"];
  loading: boolean;
  comparaisonFragment?: SearchFragmentDateMetrics;
  onClickPoint?: (
    uid: string | number | Array<string | number>,
    range: { from: Date; to: Date }
  ) => void;
  disableAnimation?: boolean;
  className?: string;
  nowDate?: AbstractDate;
  isFullscreen?: boolean;
}

function getLineChartData(
  buckets: SearchFragmentDateMetrics["data"]["aggs"]["date"]["values"],
  metric: Metric
) {
  return map(buckets, (bucket) => {
    if (bucket) {
      return {
        date: bucket.key,
        count: bucket.counts[metric],
      };
    } else {
      return null;
    }
  });
}

const CONFIGURATION = {
  hover: { mode: "index", intersect: true },
  tooltips: { mode: "index", intersect: true, position: "nearest" },
} as const;

export default function SharedWidgetsMetricLineChartInner({
  fragment,
  comparaisonFragment,
  onClickPoint,
  dataviz,
  className,
  loading,
  data,
  comparaisonData,
  disableAnimation,
  nowDate,
  isFullscreen,
}: SharedWidgetsMetricLineChartInnerProps) {
  const theme = useTheme();
  const metric = fragment.request.aggs.date.metrics[0];
  //@TODO: quick fix pour ce problème, il faudra investiguer pourquoi last_year ne fonctionne pas par défaut
  // const comparaison =
  //   useComparaisonPeriodFromFragments(fragment, comparaisonFragment, loading) ||
  //   (comparaisonFragment && "last_year");
  const comparaison =
    useComparaisonPeriodFromFragments(fragment, comparaisonFragment, loading) ||
    (comparaisonFragment && "previous_period");

  const range = useRangeWithTimeUnit(fragment, loading, nowDate);
  const previousRange = useRangeFromFragment(
    comparaisonFragment,
    loading,
    nowDate
  );
  const locale = useCurrentUserLocale();
  const metricLabel = getMetricLabel(locale, metric);
  const main_color = useMemo(
    () =>
      isColor(dataviz.color) && dataviz.color.length > 1
        ? dataviz.color
        : Color.PacificBlue,
    [dataviz.color]
  );
  const timezone = useTimezoneFromFragment(fragment);
  const series = useMemo(() => {
    const availableSeries = [
      {
        id: "post_repost",
        label: getMetricLabel(locale, metric),
        data: getLineChartData(data ? data.aggs.date.values : null, metric),
        color: main_color,
        isComparaisonPeriod: false,
      },
    ];
    if (comparaison) {
      availableSeries.push({
        id: comparaison,
        label: tr(locale, "CUSTOM_PAGES.COMPARAISON." + comparaison),
        data: getLineChartData(
          comparaisonData ? comparaisonData.aggs.date.values : null,
          metric
        ),
        color: content({ theme }, "secondary"),
        isComparaisonPeriod: true,
      });
    }
    return availableSeries;
  }, [locale, metric, data, main_color, comparaison, comparaisonData, theme]);

  return (
    <LineChart
      chartTooltip={(series) => (
        <LineChartTooltip
          dataSeries={series}
          locale={locale}
          timezone={timezone}
          withPreviousPeriod={!!comparaisonFragment}
          metricLabel={!!comparaisonFragment ? metricLabel : ""}
        />
      )}
      series={series}
      range={range}
      locale={locale}
      className={className}
      timezone={timezone}
      tooltip={(serie) => (
        <Tooltip
          range={serie.isComparaisonPeriod ? previousRange : range}
          count={serie.count}
          color={serie.color}
          label={serie.label}
          metric={metric}
          locale={locale}
        />
      )}
      config={CONFIGURATION}
      onClickPoint={onClickPoint}
      scale={dataviz.scale || "linear"}
      disableAnimation={disableAnimation}
      isFullscreen={isFullscreen}
      displayVolumes={dataviz.displayVolumes}
    />
  );
}
